import type { Notification } from '@parlay-picker/shared/notification';
import type { NotificationDisplayData } from '@parlay-picker/shared/notificationDisplay';
import type { ReactElement } from 'react';
import { COLOR_SCHEME_CLASSES } from './colorScheme';

/**
 * Pull the badge counter shown next to the summary. `streakLength` lives on
 * AccountSystemNotificationData (streak_alert); `count` lives on
 * SocialNotificationData (followed_user_bet leg count, etc.). Mutually
 * exclusive — the renderer prefers streakLength.
 */
function getBadgeCounters(n: Notification): { streakLength?: number; count?: number } {
  switch (n.type) {
    case 'streak_alert':
      return { streakLength: n.data.streakLength };
    case 'followed_user_bet':
    case 'followed_user_settled':
    case 'parlay_shared_with_you':
    case 'parlay_shared_by_you':
      return { count: n.data.count };
    default:
      return {};
  }
}

/** System/Account body: summary + details + streak/count */
export function SystemBody({
  displayData,
  notification,
}: {
  displayData: NotificationDisplayData;
  notification: Notification;
}): ReactElement {
  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];
  const { streakLength, count } = getBadgeCounters(notification);

  return (
    <div className="flex items-center gap-3 mt-2">
      {(streakLength || count) && (
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colors.iconBg}`}
        >
          <span className={`font-mono text-base font-bold ${colors.iconText}`}>
            {streakLength ? `${streakLength}W` : count}
          </span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-theme-text leading-snug">{notification.data.summary}</p>
        {notification.data.details && (
          <p className="text-xs text-theme-text-tertiary mt-1">{notification.data.details}</p>
        )}
      </div>
    </div>
  );
}
